import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.barakah.cloud';

const getAuthHeaders = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.access ? { Authorization: `Bearer ${user.access}` } : {};
};

export const getEvents = (params = {}) => {
    return axios.get(`${API_BASE_URL}/api/events/`, { 
        params,
        headers: getAuthHeaders()
    });
};

export const getLandingEvents = () => {
    return axios.get(`${API_BASE_URL}/api/events/landing/`);
};

export const getEventDetail = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/`, {
        headers: getAuthHeaders()
    });
};

export const createEvent = (formData) => {
    return axios.post(`${API_BASE_URL}/api/events/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const updateEvent = (slug, formData) => {
    const isFormData = formData instanceof FormData;
    return axios.patch(`${API_BASE_URL}/api/events/${slug}/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
        }
    });
};

export const deleteEvent = (slug) => {
    return axios.delete(`${API_BASE_URL}/api/events/${slug}/`, {
        headers: getAuthHeaders()
    });
};

export const getMyEvents = () => {
    return axios.get(`${API_BASE_URL}/api/events/my_events/`, {
        headers: getAuthHeaders()
    });
};

// Registration Logic
export const validateEventVoucher = (slug, code) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/validate_voucher/`, { code }, {
        headers: getAuthHeaders()
    });
};

export const registerForEvent = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/register/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const cancelPendingRegistration = (slug) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/cancel_pending/`, {}, {
        headers: getAuthHeaders()
    });
};

export const manualRegisterParticipant = (slug, data) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/manual_register/`, data, {
        headers: getAuthHeaders()
    });
};

export const bulkManualRegister = (slug, data) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/bulk_manual_register/`, data, {
        headers: getAuthHeaders()
    });
};

export const getEventParticipants = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/participants/`, {
        headers: getAuthHeaders()
    });
};

// Submissions (Management)
export const getEventRegistrations = (params = {}) => {
    return axios.get(`${API_BASE_URL}/api/events/registrations/`, {
        params,
        headers: getAuthHeaders()
    });
};

export const approveRegistration = (id) => {
    return axios.post(`${API_BASE_URL}/api/events/registrations/${id}/approve/`, {}, {
        headers: getAuthHeaders()
    });
};

export const rejectRegistration = (id) => {
    return axios.post(`${API_BASE_URL}/api/events/registrations/${id}/reject/`, {}, {
        headers: getAuthHeaders()
    });
};

export const toggleOrderCompleted = (id) => {
    return axios.post(`${API_BASE_URL}/api/events/registrations/${id}/toggle_order_completed/`, {}, {
        headers: getAuthHeaders()
    });
};

export const updateEventRegistration = (id, data) => {
    return axios.patch(`${API_BASE_URL}/api/events/registrations/${id}/`, data, {
        headers: getAuthHeaders()
    });
};

export const bulkDeleteRegistrations = (ids) => {
    return axios.post(`${API_BASE_URL}/api/events/registrations/bulk_delete/`, { ids }, {
        headers: getAuthHeaders()
    });
};

export const exportRegistrationsCsv = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/export_registrations/`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
    });
};

export const blastEventWhatsapp = (slug, message, registrationIds = null, imageBase64 = null) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/blast_whatsapp/`, { 
        message,
        registration_ids: registrationIds,
        image_base64: imageBase64
    }, {
        headers: getAuthHeaders()
    });
};

export const blastEventEmail = (slug, subject, message, registrationIds = null, attachments = []) => {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);
    if (registrationIds) {
        formData.append('registration_ids', JSON.stringify(registrationIds));
    }
    attachments.forEach(file => {
        formData.append('attachments', file);
    });
    return axios.post(`${API_BASE_URL}/api/events/${slug}/blast_email/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const globalBlastEmail = (subject, message, registrationIds, attachments = []) => {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('message', message);
    formData.append('registration_ids', JSON.stringify(registrationIds));
    attachments.forEach(file => {
        formData.append('attachments', file);
    });
    return axios.post(`${API_BASE_URL}/api/events/global_blast_email/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const bulkResendNotifications = (slug, registrationIds) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/bulk_resend_notifications/`, { 
        registration_ids: registrationIds 
    }, {
        headers: getAuthHeaders()
    });
};

export const getGlobalRegistrations = (params = {}) => {
    return axios.get(`${API_BASE_URL}/api/events/global_registrations/`, {
        params,
        headers: getAuthHeaders()
    });
};

export const globalBlastWhatsapp = (message, registrationIds, imageBase64 = null) => {
    return axios.post(`${API_BASE_URL}/api/events/global_blast_whatsapp/`, { 
        message,
        registration_ids: registrationIds,
        image_base64: imageBase64
    }, {
        headers: getAuthHeaders()
    });
};

export const addDocumentationImages = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/add_documentation_images/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const deleteDocumentationImage = (slug, imageId) => {
    return axios.delete(`${API_BASE_URL}/api/events/${slug}/delete_documentation_image/`, {
        data: { image_id: imageId },
        headers: getAuthHeaders()
    });
};

export const addGalleryImages = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/add_gallery_images/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const deleteGalleryImage = (slug, imageId) => {
    return axios.delete(`${API_BASE_URL}/api/events/${slug}/delete_gallery_image/`, {
        data: { image_id: imageId },
        headers: getAuthHeaders()
    });
};

export const getCertificateSettings = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/certificate_settings/`, {
        headers: getAuthHeaders()
    });
};

export const updateCertificateSettings = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/certificate_settings/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const downloadCertificate = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/download_certificate/`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
    });
};

export const getBibSettings = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/bib_settings/`, {
        headers: getAuthHeaders()
    });
};

export const updateBibSettings = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/bib_settings/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const downloadBib = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/download_bib/`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
    });
};

export const searchUsers = (search = '') => {
    return axios.get(`${API_BASE_URL}/api/auth/users/`, {
        params: { search },
        headers: getAuthHeaders()
    });
};

export const getAvailableUsers = (slug, search = '', page = 1, pageSize = 10, signal = null) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/available-users/`, {
        params: { search, page, page_size: pageSize },
        headers: getAuthHeaders(),
        signal
    });
};

export const importParticipantsCsv = (slug, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE_URL}/api/events/${slug}/import_participants_csv/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const manageCommittee = (slug, userId, operation, identifier = null) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/manage-committees/`, {
        user_id: userId,
        operation,
        identifier
    }, {
        headers: getAuthHeaders()
    });
};

export const sendScanLink = (slug, userIds = null) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/send-scan-link/`, {
        user_ids: userIds
    }, {
        headers: getAuthHeaders()
    });
};

export const getUserLabels = () => {
    return axios.get(`${API_BASE_URL}/api/auth/labels/`, {
        headers: getAuthHeaders()
    });
};

export const getSpecialQRSettings = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/special_qr_settings/`, {
        headers: getAuthHeaders()
    });
};

export const updateSpecialQRSettings = (slug, formData) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/special_qr_settings/`, formData, {
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
        }
    });
};

export const regenerateQRImages = (slug) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/regenerate_qr_images/`, {}, {
        headers: getAuthHeaders()
    });
};
export const markEventSessionFinished = (slug, sessionId) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/mark_session_finished/`, { session_id: sessionId }, {
        headers: getAuthHeaders()
    });
};

export const toggleLikeEvent = (slug) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/like/`, {}, {
        headers: getAuthHeaders()
    });
};

export const getEventTestimonies = (slug) => {
    return axios.get(`${API_BASE_URL}/api/events/${slug}/testimonies/`);
};

export const submitEventTestimony = (slug, rating, comment) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/submit_testimony/`, { rating, comment }, {
        headers: getAuthHeaders()
    });
};

export const completeEvent = (slug, blastMessage, useDefault) => {
    return axios.post(`${API_BASE_URL}/api/events/${slug}/complete_event/`, {
        blast_message: blastMessage,
        use_default: useDefault
    }, {
        headers: getAuthHeaders()
    });
};

